import React, { CSSProperties } from "react";
import styled, { css } from "styled-components";
import { SharedToggleProps } from "@shared/common/Toggle/types";
import useToggle from "@shared/common/Toggle/useToggle";
import { hexToRGB } from "@shared/utils/misc";

interface SegmentedToggleProps extends SharedToggleProps {
  style?: CSSProperties;
  /** Tints the selected option (e.g. long = green, short = red). */
  optionToTone?: Record<string, SegmentedToggleTone>;
}

export type SegmentedToggleTone = "green" | "red";

/**
 * New-look segmented control for small form choices (trade type, direction):
 * a quiet neutral track with the selected option raised on a white thumb.
 * Same API as the classic `SwitchSelect`, so forms can swap one for the other.
 */
const SegmentedToggle = ({
  options,
  selectedOption: propsSelectedOption,
  initialOption,
  onClick,
  disabled,
  invalidOptions,
  optionToDisplayName,
  optionToTone,
  style,
}: SegmentedToggleProps) => {
  const { changeOption, selectedOption } = useToggle({
    options,
    initialOption,
    onClick,
    disabled,
    selectedOption: propsSelectedOption,
  });

  return (
    <Track role="radiogroup" $disabled={disabled} style={style}>
      {options?.map((option) => {
        const selected = selectedOption === option;
        return (
          <Option
            key={option}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            $selected={selected}
            $invalid={invalidOptions?.includes(option)}
            $tone={selected ? optionToTone?.[option] : undefined}
            onClick={() => changeOption?.(option)}
          >
            {optionToDisplayName ? optionToDisplayName[option] : option}
          </Option>
        );
      })}
    </Track>
  );
};

export default SegmentedToggle;

const Track = styled.div<{ $disabled?: boolean }>`
  display: flex;
  width: 100%;
  /* In a narrow column the track grows to fit its options instead of
     letting the last one run into its edge. */
  min-width: max-content;
  height: 32px;
  padding: 2px;
  gap: 2px;
  border-radius: ${(p) => (p.theme.isNewLook ? 8 : 999)}px;
  background: ${(p) => p.theme.colors.cardLightBgColor};
  box-shadow: inset 0 0 0 1px ${(p) => p.theme.colors.lightestBorderGrey};
  ${(p) =>
    p.$disabled &&
    `
    opacity: 0.5;
    cursor: not-allowed;
  `}
`;

const Option = styled.button<{
  $selected: boolean;
  $invalid?: boolean;
  $tone?: SegmentedToggleTone;
}>`
  flex: 1 1 0;
  min-width: max-content;
  padding: 0 12px;
  border: none;
  border-radius: ${(p) => (p.theme.isNewLook ? 6 : 999)}px;
  background: transparent;
  font: inherit;
  font-size: 13px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(p) => p.theme.colors.secondaryText};
  cursor: pointer;
  transition: color 150ms, background-color 150ms, box-shadow 150ms;
  &:hover:not(:disabled) {
    color: ${(p) => p.theme.colors.primaryText};
  }
  &:disabled {
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.4)};
    outline-offset: 1px;
  }
  ${(p) =>
    p.$selected &&
    css`
      background: ${p.theme.isDarkMode
        ? hexToRGB(p.theme.colors.secondaryText, 0.2)
        : p.theme.colors.cardBgColor};
      color: ${p.$tone ? p.theme.colors[p.$tone] : p.theme.colors.titleColor};
      font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
      box-shadow: 0 1px 2px ${hexToRGB(p.theme.colors.titleColor, 0.1)};
      &:hover:not(:disabled) {
        color: ${p.$tone ? p.theme.colors[p.$tone] : p.theme.colors.titleColor};
      }
    `}
  ${(p) => p.$invalid && `opacity: 0.35;`}
`;
